import type { AppConfig } from '@/types/config';

/** Catalog viewTypes that may appear as a URL path segment in suggestion image URLs. */
const KNOWN_CATALOG_VIEW_TYPES = [
    'small',
    'medium',
    'large',
    'hi-res',
    'hires',
    'thumbnail',
    'swatch',
    'zoom',
] as const;

/**
 * Rewrites a catalog image URL path segment to the configured viewType.
 *
 * SCAPI `getSearchSuggestions` returns a single image (often `medium`), e.g.
 * `.../default/medium/product.jpg`. When `search.suggestions.images.viewType`
 * is `large`, this produces `.../default/large/product.jpg`.
 */
export function applySearchSuggestionImageViewType(
    src: string | undefined,
    targetViewType: string | undefined
): string | undefined {
    if (!src || !targetViewType?.trim()) {
        return src;
    }

    const target = targetViewType.trim();
    if (KNOWN_CATALOG_VIEW_TYPES.some((viewType) => viewType.toLowerCase() === target.toLowerCase())) {
        const pathSegments = src.split('/');
        const sourceIndex = pathSegments.findIndex((segment) =>
            KNOWN_CATALOG_VIEW_TYPES.some((viewType) => viewType.toLowerCase() === segment.toLowerCase())
        );

        if (sourceIndex !== -1) {
            pathSegments[sourceIndex] = target;
            return pathSegments.join('/');
        }
    }

    return src;
}

export function resolveSearchSuggestionImageSrc(src: string | undefined, config: AppConfig): string | undefined {
    const viewType = config.search?.suggestions?.images?.viewType;
    return applySearchSuggestionImageViewType(src, viewType);
}
